import { EnHeading } from 'components/atoms/enHeading';

import branding from 'assets/images/services/branding.png';
import businessDesign from 'assets/images/services/business-design.png';
import customerExperienceImprovement from 'assets/images/services/customer-experience-improvement.png';
import designOps from 'assets/images/services/design-ops.png';
import employeeExperienceDesign from 'assets/images/services/employee-experience-design.png';
import mvvDesign from 'assets/images/services/mvv-design.png';
import { JaHeading } from 'components/atoms/jaHeading';
import { Text } from 'components/atoms/text';
import { ContentWrapper } from 'components/organisms/contentWrapper';
import FadeUpSection from 'components/organisms/fadeUpSection';
import { InquiryBlock } from 'components/organisms/inquiryBlock';
import ParticleImageManager from 'components/organisms/particleCanvas/ParticleImageManager';
import { graphql } from 'gatsby';
import React, { useEffect } from 'react';
import { text } from 'style/mixin';
import sizes from 'style/sizes';
import { breakpoint, mq } from 'style/variables';
import { ServiceIndexQuery } from 'types/graphql-types';
import SEO from 'util/seo';

import styled from '@emotion/styled';

interface IndexPageProps {
  data: ServiceIndexQuery;
}

const TitleSection = styled('div')`
  width: 100%;
  ${mq.onlypc} {
    padding-top: ${sizes.margin[180]};
    padding-bottom: ${sizes.margin[100]};
  }
  ${mq.onlysp} {
    width: unset;
    padding-top: ${sizes.margin[155]};
    padding-bottom: ${sizes.margin[64]};
  }
`;
const Section = styled.section`
  margin-bottom: 180px;

  ${mq.onlysp} {
    margin-bottom: 120px;
  }
`;
const InfoBoxSection = styled.div`
  display: block;
`;
const InfoBox = styled.div`
  position: relative;

  ${mq.onlypc} {
    display: grid;
    grid-template-columns: auto 1fr;
    grid-template-rows: auto auto 1fr;
    align-items: start;
  }

  & + & {
    margin-top: ${sizes.margin['160']};

    ${mq.onlysp} {
      margin-top: 110px;
    }
  }
`;
const InfoBoxCategory = styled(EnHeading)`
  grid-column: 1 / 2;
  ${text(56, 1)}
  margin-bottom: 26px;
  letter-spacing: -0.02em;
  ${mq.onlysp} {
    margin-bottom: 18px;
  }
`;
const InfoBoxHeader = styled(JaHeading)`
  grid-column: 1 / 2;
  ${text(28, 1.5)}
  font-weight: 600;
  letter-spacing: 0.09em;
  ${mq.onlysp} {
    ${text(18, 1.5)}
    font-weight: 600;
  }
`;
const InfoBoxIcon = styled.div`
  grid-column: 2 / 3;
  grid-row: 1 / 4;
  display: flex;
  justify-content: flex-end;
  align-self: center;
  img {
    width: 330px;
    height: auto;
    object-fit: cover;
    display: block;

    ${mq.onlysp} {
      width: 180px;
    }
  }

  ${mq.onlypc} {
    min-width: 330px;
  }
  ${mq.onlysp} {
    display: flex;
    justify-content: center;
    margin-top: 40px;
    margin-left: 33px;
  }
`;
const InfoBoxContent = styled(Text)`
  grid-column: 1 / 2;
  grid-row: 3;
  margin-top: 50px;
  ${mq.onlypc} {
    ${text(18, 2.12)}
    width: 688px;
  }
  ${mq.onlysp} {
    ${text(16, 1.8)}
    margin-left: 33px;
  }
`;

const infoBoxData: Array<{
  category: string | React.ReactNode;
  header: React.ReactNode;
  icon: string;
  content: string | React.ReactNode;
}> = [
  {
    category: 'Business Design',
    header: <>สร้างสรรค์ประสบการณ์ใหม่โดยยึดความเข้าใจผู้ใช้เป็นหลัก และออกแบบให้เกิดคุณค่าทางธุรกิจ</>,
    icon: businessDesign,
    content:
      'เราสร้างสรรค์ภาพรวมของประสบการณ์ที่ผู้ใช้ต้องการ ตลอดจนไอเดียเกี่ยวกับฟังก์ชันและกลยุทธ์ที่จำเป็น โดยไม่ยึดติดกับข้อจำกัดของช่องทางและระบบที่มีอยู่เดิม แต่ใช้ข้อมูลจริงที่ได้จากการวิจัยเป็นพื้นฐาน นอกจากนี้ เรายังคำนึงถึงความเป็นไปได้ในเชิงธุรกิจและเชิงเทคนิค เพื่อช่วยสนับสนุนการวางโร้ดแมพ (Roadmap) และการวางแนวคิดด้านสถาปัตยกรรม (Architecture) สำหรับการพัฒนาบริการหรือผลิตภัณฑ์',
  },
  {
    category: (
      <>
        Customer Experience
        <br />
        Improvement
      </>
    ),
    header: <>ทำความเข้าใจการใช้บริการที่มีอยู่ในปัจจุบัน และปรับปรุงเพื่อให้บรรลุเป้าหมายที่วางไว้</>,
    icon: customerExperienceImprovement,
    content: (
      <>
        เราวิเคราะห์ข้อมูลการใช้งานและวิจัยผู้ใช้เพื่อทำความเข้าใจช่องว่างระหว่างประสบการณ์ที่ได้รับจากบริการปัจจุบันกับความต้องการของผู้ใช้
        และกำหนดนิยามของ "ประสบการณ์ที่ควรจะเป็น" ขึ้นมาใหม่
        <br />
        นอกจากนี้
        เรายังระบุฟังก์ชันที่จำเป็นทั้งหมด
        ตั้งแต่ขั้นตอนการรับรู้ถึงบริการไปจนถึงกระบวนการใช้งานที่เกี่ยวข้องทั้งหมด
        พร้อมทั้งวางแผนการพัฒนาและโครงสร้างทีมเพื่อการปรับปรุงอย่างต่อเนื่อง
        รวมถึงให้การสนับสนุนด้านการวิเคราะห์และปรับปรุงแก้ไขหลังจากเปิดตัวบริการไปแล้ว
      </>
    ),
  },
  {
    category: 'MVV Design',
    header: <>วิเคราะห์จุดแข็งของบริการหรือธุรกิจ เพื่อกำหนดยุทธศาสตร์และทิศทางในอนาคต</>,
    icon: mvvDesign,
    content: (
      <>
        เรากำหนดวิสัยทัศน์ขององค์กรหรือบริการ และวางแนวคิดหลัก (Concept)
        เพื่อใช้เป็นรากฐานสำคัญในการสร้างสรรค์ธุรกิจและพิจารณากลยุทธ์ในอนาคต
        <br />
        แนวคิดนี้ไม่เพียงแต่จะใช้เป็นแนวทางในการตัดสินใจเพื่อขับเคลื่อนธุรกิจและเป็นหลักปฏิบัติงานของพนักงานเท่านั้น
        แต่เรายังวางแผนและพัฒนามาตรการเพื่อสื่อสารทิศทางที่องค์กรมุ่งหวังให้เข้าถึงและเป็นที่เข้าใจในกลุ่มผู้มีส่วนได้ส่วนเสีย
        (Stakeholders) ทั้งภายในและภายนอกองค์กร
      </>
    ),
  },
  {
    category: (
      <>
        Employee
        <br />
        Experience Design
      </>
    ),
    header: <>การทำความเข้าใจพนักงาน ขั้นตอนการทำงานและวัฒนธรรมขององค์กร เพื่อสร้างสรรค์ประสบการณ์การทำงานที่ดีที่สุด</>,
    icon: employeeExperienceDesign,
    content:
      'เราศึกษาตัวงานและพนักงานในปัจจุบันจากหลากหลายมุมมอง เพื่อนำมากำหนด "ประสบการณ์พนักงานในอุดมคติ" ที่ไม่ได้มองแค่เรื่องประสิทธิภาพ แต่ยังใส่ใจในแง่มุมทางจิตวิทยาของการทำงานด้วย จากนั้นเราจึงออกแบบประสบการณ์ที่ส่งเสริมให้คนในองค์กรทำงานได้อย่างเต็มศักยภาพและมีความคิดริเริ่ม ผ่านการช่วยพัฒนา UX/UI ของระบบงานไปจนถึงการออกแบบสภาพแวดล้อมในที่ทำงาน เช่น ออฟฟิศ เป็นต้น',
  },
  {
    category: 'Branding',
    header: (
      <>
        สร้างอัตลักษณ์และคุณค่าของแบรนด์
        <br />
        พร้อมเสริมสร้างความผูกพันกับผู้ใช้งาน
      </>
    ),
    icon: branding,
    content:
      'เราเริ่มต้นจากการทำความเข้าใจอย่างถ่องแท้ถึงตัวตนของบริษัท ผลิตภัณฑ์ และบริการ ตลอดจนกลุ่มลูกค้าและผู้ใช้งานเป้าหมาย เพื่อออกแบบการสื่อสารทั้งหมดที่จะสะท้อนคุณค่าของแบรนด์ออกไปได้อย่างแม่นยำที่สุด โดยเราให้บริการตั้งแต่การวางแนวคิดหลัก (Concept) ของแบรนด์ การสร้างสรรค์โลโก้และภาพลักษณ์ การวางระบบดีไซน์ ไปจนถึงการออกแบบเนื้อหา (Content) ต่างๆ',
  },
  {
    category: 'Design Ops',
    header: (
      <>
        สนับสนุนการเติบโตทางธุรกิจ
        <br />
        ผ่านการปรับปรุงบริการอย่างต่อเนื่อง
      </>
    ),
    icon: designOps,
    content: (
      <>
        เราเข้ามาเป็นผู้รับผิดชอบหลักในการดำเนินงานบริการอย่างต่อเนื่อง
        และมุ่งมั่นทุ่มเทเพื่อการเติบโตของธุรกิจในระยะกลางถึงระยะยาว
        <br />
        เราสนับสนุนการวางแผนและดำเนินงานตามวงจรการปรับปรุง ตั้งแต่การวิเคราะห์ข้อมูลการใช้งาน,
        การสร้างสรรค์มาตรการจากมุมมองผู้ใช้, การพัฒนาฟังก์ชันใหม่ ไปจนถึงการประเมินผล
        พร้อมทั้งช่วยวางโครงสร้างทีมที่จำเป็นเพื่อให้กระบวนการทั้งหมดนี้สำเร็จลุล่วง
      </>
    ),
  },
];

const Pages: React.FC<IndexPageProps> = () => {
  let isSp = false;
  if (typeof window !== 'undefined') isSp = window.innerWidth <= breakpoint;

  useEffect(() => {
    ParticleImageManager.loadAll().then(() => {
      ParticleImageManager.generateAll().then(() => {
        ParticleImageManager.start();
      });
    });

    return () => {
      ParticleImageManager.delete();
    };
  }, []);

  return (
    <>
      <SEO title={'Services'} />
      <ContentWrapper>
        <TitleSection>
          <EnHeading>Services</EnHeading>
        </TitleSection>
        <Section>
          <FadeUpSection>
            <InfoBoxSection>
              {infoBoxData.map((info, idx) => (
                <InfoBox key={idx}>
                  <InfoBoxCategory>{info.category}</InfoBoxCategory>
                  <InfoBoxHeader>{info.header}</InfoBoxHeader>
                  <InfoBoxContent>{info.content}</InfoBoxContent>
                  <InfoBoxIcon>
                    <img src={info.icon} alt="" />
                  </InfoBoxIcon>
                </InfoBox>
              ))}
            </InfoBoxSection>
          </FadeUpSection>
        </Section>
      </ContentWrapper>

      <InquiryBlock th />
    </>
  );
};

export const pageQuery = graphql`
  query ServiceIndexTh {
    site {
      siteMetadata {
        title
      }
    }
  }
`;

export default Pages;
